import type { AnnotatedJsonSchema7 } from '@web-config-app/core';
import {
  mockTextInputControlProperty,
  mockDateInputControlProperty,
  mockDateTimeInputControlProperty,
  mockNumberInputControlProperty,
  mockIntegerInputControlProperty,
  mockBooleanInputControlProperty,
  mockSelectInputControlProperty,
  mockRichTextInputControlProperty,
  mockJsonLogicInputControlProperty,
} from './input-control-mock-schemas';

export const mockEntitySchema = {
  title: 'Mock Entity Schema',
  description:
    'A schema used for tests including properties for all control types. This schema only contains primitive types.',
  'x-entity-presentation': {
    hint: {
      key: 'MOCK_COMPLEX_COMPOUND_OBJECT_HINT',
    },
    banner: {
      title: {
        key: 'MOCK_BANNER_TITLE_GOOD_TO_KNOW',
      },
      status: 'info',
      description: {
        key: 'MOCK_COMPLEX_OBJECT_BANNER_DESCRIPTION',
      },
    },
  },
  type: 'object',
  properties: {
    primitiveObject: {
      type: 'object',
      title: 'primitive object title',
      'x-entity-presentation': {
        hint: {
          key: 'MOCK_PRIMITIVE_OBJECT_HINT',
        },
      },
      properties: {
        text: mockTextInputControlProperty,
        date: mockDateInputControlProperty,
        number: mockNumberInputControlProperty,
        integer: mockIntegerInputControlProperty,
        select: mockSelectInputControlProperty,
        boolean: mockBooleanInputControlProperty,
      },
    },
    primitiveObjectTwo: {
      type: 'object',
      title: 'primitive object title two',
      'x-entity-label': {
        key: 'MOCK_PRIMITIVE_OBJECT_LABEL_KEY',
      },
      'x-entity-presentation': {
        hint: {
          key: 'MOCK_PRIMITIVE_OBJECT_HINT',
        },
      },
      properties: {
        text: mockTextInputControlProperty,
        date: mockDateInputControlProperty,
      },
    },
  },
};

export const mockArrayPrimitiveObjectSchema = {
  type: 'array',
  title: 'Mock array primitive object',
  'x-entity-label': {
    key: 'MOCK_ARRAY_PRIMITIVE_OBJECT_LABEL',
  },
  'x-entity-presentation': {
    hint: {
      key: 'MOCK_ARRAY_PRIMITIVE_OBJECT_HINT',
    },
    banner: {
      title: {
        key: 'MOCK_ARRAY_PRIMITIVE_BANNER_TITLE',
      },
      status: 'info',
      description: {
        key: 'MOCK_ARRAY_PRIMITIVE_BANNER_TEXT',
      },
    },
  },
  items: {
    type: 'object',
    title: 'A primitive object',
    'x-entity-label': {
      key: 'MOCK_PRIMITIVE_OBJECT_LABEL_KEY',
    },
    'x-entity-presentation': {
      hint: {
        key: 'MOCK_PRIMITIVE_OBJECT_HINT',
      },
    },
    properties: {
      date: mockDateInputControlProperty,
      integer: mockIntegerInputControlProperty,
    },
  },
};

const mockArrayPrimitiveSchema = {
  type: 'array',
  title: 'I am a primitive array',
  'x-entity-presentation': {
    hidden: false,
    hint: {
      key: 'MOCK_PRIMITIVE_ARRAY_HINT',
    },
  },
  items: {
    type: 'string',
    title: 'item',
  },
};
const combinatorDiscriminator = {
  propertyName: 'componentType',
  'x-entity-label': {
    key: 'LOCATION_TYPE',
  },
  'x-entity-presentation': {
    hint: {
      key: 'LOCATION_TYPE_HINT',
    },
    enumLabels: {
      enumControlPlaceholder: 'SELECT_LOCATION_OPTION',
    },
  },
};

// TODO: it would be great to automatically have fields that match the
// discriminator.propertyName hidden, rather than having to manually annotate them
// follow up ticket here: https://everlong.atlassian.net/browse/CACT-882
const combinatorOptions = [
  {
    type: 'object',
    title: 'Address',
    properties: {
      componentType: {
        type: 'string',
        enum: ['address'],
        'x-entity-label': { key: 'ADDRESS_KEY' },
        'x-entity-presentation': { hidden: true },
      },
      street_address: {
        type: 'string',
        'x-entity-label': { key: 'STREET_ADDRESS' },
      },
      city: {
        type: 'string',
        'x-entity-label': { key: 'CITY' },
      },
      state: {
        type: 'string',
        'x-entity-label': { key: 'STATE' },
      },
    },
    required: ['street_address', 'city', 'state', 'componentType'],
  },
  {
    type: 'object',
    title: 'User',
    properties: {
      componentType: {
        type: 'string',
        enum: ['user'],
        'x-entity-label': { key: 'USER_KEY' },
        'x-entity-presentation': { hidden: true },
      },
      name: {
        type: 'string',
        'x-entity-label': { key: 'NAME' },
      },
      mail: {
        type: 'string',
        'x-entity-label': { key: 'MAIL' },
      },
    },
    required: ['name', 'mail', 'componentType'],
  },
] as AnnotatedJsonSchema7[];

const mockCombinatorObjectSchema: AnnotatedJsonSchema7 = {
  type: 'object',
  title: 'I am an object combinator',
  'x-entity-label': {
    key: 'LOCATION',
  },
  discriminator: combinatorDiscriminator,
  oneOf: combinatorOptions,
};

export const mockCombinatorOneOfArraySchema: AnnotatedJsonSchema7 = {
  type: 'array',
  title: 'I am an array combinator',
  'x-entity-label': {
    key: 'LOCATION',
  },
  items: {
    discriminator: combinatorDiscriminator,
    type: 'object',
    'x-entity-label': {
      key: 'MOCK_A_COMPONENT',
    },
    required: ['componentType'],
    oneOf: combinatorOptions,
  },
};

export const mockComplexObjEntitySchema = {
  type: 'object',
  title: 'Mock Entity Schema',
  description:
    'A schema used for tests including properties for all control types. This schema only contains primitive types.',
  'x-entity-label': {
    key: 'MOCK_COMPLEX_OBJECT_LABEL_KEY',
  },
  'x-entity-presentation': {
    hidden: false,
    hint: {
      key: 'MOCK_COMPLEX_OBJECT_HINT',
    },
    banner: {
      title: {
        key: 'MOCK_BANNER_TITLE_GOOD_TO_KNOW',
      },
      status: 'info',
      description: {
        key: 'MOCK_COMPLEX_OBJECT_BANNER_DESCRIPTION',
      },
    },
  },
  properties: {
    text: mockTextInputControlProperty,
    jsonLogic: mockJsonLogicInputControlProperty,
    richText: mockRichTextInputControlProperty,
    date: mockDateInputControlProperty,
    dateTime: mockDateTimeInputControlProperty,
    number: mockNumberInputControlProperty,
    integer: mockIntegerInputControlProperty,
    primitiveObject: mockEntitySchema,
    arrayPrimitiveObject: mockArrayPrimitiveObjectSchema,
    complexObject: {
      type: 'object',
      title: 'complex object',
      'x-entity-label': {
        key: 'MOCK_COMPLEX_OBJECT_TWO_LABEL_KEY',
      },
      'x-entity-presentation': {
        hidden: false,
        hint: {
          key: 'MOCK_COMPLEX_COMPOUND_OBJECT_HINT',
        },
      },
      properties: {
        object: {
          ...mockEntitySchema,
          properties: {
            ...mockEntitySchema.properties,
            arrayPrimitive: mockArrayPrimitiveSchema,
          },
        },
      },
    },
    combinatorObject: mockCombinatorObjectSchema,
    combinatorArrayAnyOf: mockCombinatorOneOfArraySchema,
  },
};
